import { useState, useRef, useEffect } from "react";
import { processSchemaWithPipeline } from "@/pages/AlgoManagement/schema-transformations";

interface ProcessedSchema {
  schema: any;
  processedAt: number;
}

interface UseSchemaProcessorResult {
  processedSchema: any | null;
  isLoading: boolean;
  error: Error | null;
}

// Check localStorage for debug mode (initialize if missing)
let DEBUG = false;
if (typeof window !== "undefined") {
  const debugValue = localStorage.getItem("DEBUG_JSONFORMS");
  if (debugValue === null) {
    // Key doesn't exist, initialize to "false"
    localStorage.setItem("DEBUG_JSONFORMS", "false");
    console.log(
      "💡 JSON Forms debug mode initialized to OFF. To enable detailed schema logs, run:\n" +
        "  localStorage.setItem('DEBUG_JSONFORMS', 'true')\n" +
        "Then reload the page.",
    );
  }
  DEBUG = debugValue === "true";
}

/**
 * Hook to process JSON schemas with caching and memoization
 *
 *
 * Pipeline steps:
 * 1. dereferenceSchema - Resolve $ref pointers
 * 2. mergeAllOf - Flatten allOf combinators (conditional)
 * 3. normalizeScalarAnyOf - Flatten scalar anyOf, transform additionalProperties
 * 4. transformObjectAnyOf - Convert object anyOf to oneOf with discriminators
 *
 * @param rawSchema - The raw JSON schema to process
 * @returns Object containing processed schema, loading state, and error
 *
 * @example
 * ```ts
 * const { processedSchema, isLoading, error } = useSchemaProcessor(rpcSchema);
 *
 * // Enable debug mode in browser console:
 * localStorage.setItem('DEBUG_JSONFORMS', 'true');
 * // Then reload page to see detailed processing logs
 * ```
 */
export const useSchemaProcessor = (
  rawSchema: any,
): UseSchemaProcessorResult => {
  const cacheRef = useRef<Map<string, ProcessedSchema>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [processedSchema, setProcessedSchema] = useState<any>(null);

  useEffect(() => {
    if (!rawSchema) {
      setProcessedSchema(null);
      return;
    }

    // Create cache key from schema
    const cacheKey = JSON.stringify(rawSchema);
    const cached = cacheRef.current.get(cacheKey);

    // Return cached if less than 5 minutes old
    if (cached && Date.now() - cached.processedAt < 300000) {
      setProcessedSchema(cached.schema);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Process schema using the new modular pipeline
    processSchemaWithPipeline(rawSchema, { debug: DEBUG })
      .then((result) => {
        // Cache result
        cacheRef.current.set(cacheKey, {
          schema: result.schema,
          processedAt: Date.now(),
        });

        if (DEBUG) {
          console.log(
            "%c📊 Pipeline Statistics",
            "font-weight: bold; font-size: 13px; color: #00bcd4; background: #1a1a1a; padding: 6px 10px; border-radius: 3px;",
          );
          console.log(
            `%c⏱️  Processing time: ${result.processingTime}ms`,
            "color: #ff9800; font-weight: bold;",
          );

          // Create summary table
          const summary = result.metadata.map((meta) => ({
            "Step": meta.name,
            "Status": meta.skipped ? "⏭️ Skipped" : "✅ Complete",
            "Keys Changed": meta.transformedKeys.length,
            "Details": meta.reason ||
              (meta.transformedKeys.length > 0
                ? `${meta.transformedKeys.length} keys`
                : "-"),
          }));

          console.table(summary);
        }

        setProcessedSchema(result.schema);
        setIsLoading(false);
      })
      .catch((err: any) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsLoading(false);
      });
  }, [rawSchema]);

  return { processedSchema, isLoading, error };
};
